/**
 * Library - Main library view for browsing and searching notes
 */
import { uiLogger } from '@annote/core/utils/debug';
import React, { Fragment, useEffect, useState, useContext, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useDrop } from 'react-dnd';
import { Note, TypeOfNote, TYPES_OF_NOTES, NOTE_TAB_LABELS, UUID } from '@annote/types';
import { text, colors } from '@annote/core/styles';
import { useDragging } from 'src/context/DraggingContext';
import { SearchContext } from 'src/context/SearchContext';
import { useBrainContext } from 'src/context/BrainContext';
import { ShownIdsContext } from 'src/context/ShownContext';
import { scrollToNote } from 'src/helpers/scrolling';
import { FocusContext } from 'src/context/FocusContext';
import { noteCache } from 'src/context/noteCache';
import { useNotes } from 'src/context/noteCacheHooks';
import { ChevronDown } from 'lucide-react';
import { usePanelContext } from 'src/context/PanelContext';

import { CardList, ListCard } from './Cards/ListCard';
import PreviewCard from './Cards/PreviewCard';

const LibraryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 36rem;
  height: 100%;
  padding: 4.5rem 2rem 2rem 2rem;
  background: ${colors.bg.library};
  z-index: 20;
  box-shadow: ${colors.shadow.toolbar};
  border-radius: 0.75rem 0 0.75rem 0.75rem;
`;

const HideDropZone = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${colors.bg.dropzone};
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CardWrapper = styled.div`
  cursor: pointer;
  position: relative;
  z-index: auto;

  &:hover {
    z-index: 20;
  }
`;

const OverlapWrapper = styled.div`
  height: 8px; // This creates the overlap effect
  /* margin-top: -4px; // Compensate for the height to maintain overall spacing */
  position: relative;
`;

const CardGrid = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  overflow-y: auto;
  overflow-x: hidden;
  gap: 4.25rem;
  width: 38rem;
  flex: 1;
  min-height: 0;
`;

const TypeRow = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-top: 1rem;
`;

const EmptyResponse = styled.div`
  color: ${colors.text.placeholder};
  font-size: ${text.size.primary};
  font-weight: ${text.weight.medium};
`;

const TypeButton = styled.div<{ isSelected: boolean }>`
  font-weight: ${text.weight.medium}; //500
  font-size: ${text.size.primary};
  line-height: ${text.height.tight};
  padding-bottom: 0.5rem;
  text-align: center;
  border-bottom: 2px solid transparent;
  color: ${colors.text.button};
  ${(props) => (props.isSelected ? `border-bottom: 2px solid ${colors.text.primary};` : '')}
  margin: 1rem 1.5rem;
  text-transform: capitalize;

  cursor: pointer;

  &:hover {
    border-bottom: 2px solid ${colors.icon.primary};
  }
`;

const CardGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 35rem;
  padding-left: 2px;
`;

const CardGroupTitle = styled.span`
  color: ${colors.text.button};
  font-family: ${text.family.primary};
  font-size: ${text.size.small};
  font-style: normal;
  font-weight: ${text.weight.medium}; //500
  line-height: ${text.height.primary};
  text-transform: capitalize;
  padding-bottom: 1rem;
`;

const SelectedGroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding-bottom: 1rem;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const ChevronIcon = styled(ChevronDown)<{ $isExpanded: boolean }>`
  color: ${colors.text.button};
  transition: transform 0.2s ease;
  transform: ${(props) => (props.$isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)')};
  flex-shrink: 0;
`;

const SelectedGroupTitle = styled(CardGroupTitle)`
  padding-bottom: 0;
  margin: 0;
`;

const DivideLine = styled.div`
  opacity: 0.5;
  background: linear-gradient(180deg, rgba(173, 173, 173, 0.6) 0%, rgba(217, 217, 217, 0) 98.96%);
  height: 2px;
  width: 100%;
`;

type DateGroup = {
  label: string;
  minDate: string;
  notes: Note[];
};

// Groups notes into relative date buckets
// Assumes that notes are ordered by createdat in descending order
const groupByDate = (sortedNotes: Note[]): DateGroup[] => {
  const now = new Date();
  const dateGroups: DateGroup[] = [
    {
      label: 'just now',
      minDate: new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours()).toISOString(),
      notes: [],
    },
    { label: 'today', minDate: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString(), notes: [] },
    {
      label: 'yesterday',
      minDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).toISOString(),
      notes: [],
    },
    {
      label: 'previous 7 days',
      minDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).toISOString(),
      notes: [],
    },
    {
      label: 'previous 30 days',
      minDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30).toISOString(),
      notes: [],
    },
    {
      label: 'previous 90 days',
      minDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 90).toISOString(),
      notes: [],
    },
    { label: 'this year', minDate: new Date(now.getFullYear(), 0, 1).toISOString(), notes: [] },
    { label: 'alltime', minDate: new Date('1/1/2022').toISOString(), notes: [] },
  ];
  let currentGroupNum = 0;
  const nullGroupNum = 0;

  sortedNotes.forEach((note) => {
    if (!note) return;
    if (!note.syncedAt) {
      dateGroups[nullGroupNum].notes.push(note);
      return;
    }
    for (let i = currentGroupNum; i < dateGroups.length; i += 1) {
      const currentGroup = dateGroups[i];
      if (note.syncedAt > currentGroup.minDate) {
        dateGroups[currentGroupNum].notes.push(note);
        break;
      } else {
        currentGroupNum += 1;
      }
    }
  });
  return dateGroups.filter((group) => group.notes.length > 0);
};

const SELECTED_CARDS_EXPANDED_KEY = 'annote:library:selectedCardsExpanded';

const Library: React.FC<{ keepShownNotes?: boolean }> = ({ keepShownNotes = true }) => {
  const { searchTerm, typeFilter, setTypeFilter } = useContext(SearchContext);
  const { panels, closePanel } = usePanelContext();
  const [groupedSearchResults, setGroupedSearchResults] = useState<DateGroup[]>([]);
  const { searchNotes } = useBrainContext();
  const { show, hide, shownIds, shownNotes } = useContext(ShownIdsContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredCard, setHoveredCard] = useState<Note | null>(null);
  const [isLibraryHovered, setIsLibraryHovered] = useState<boolean>(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const { isUserDragging, isOverLibrary } = useDragging();
  const { setFocus } = useContext(FocusContext);

  // Local storage state for selected cards expansion
  const [isSelectedExpanded, setIsSelectedExpanded] = useState<boolean>(() => {
    const stored = localStorage.getItem(SELECTED_CARDS_EXPANDED_KEY);
    return stored === null ? true : stored === 'true';
  });

  // Store only IDs from search results
  const [searchResultIds, setSearchResultIds] = useState<UUID[]>([]);
  const [isLoadingResults, setIsLoadingResults] = useState<boolean>(false);

  // Infinite scroll state
  const [hasMoreNotes, setHasMoreNotes] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  // Get actual note data from cache using hooks - automatically subscribes and fetches if needed!
  const searchResults = useNotes(searchResultIds);
  // shownNotes comes from ShownContext - already fetched there!

  // Fetch and set search results
  const fetchSearchResults = useCallback(async (): Promise<void> => {
    setIsLoadingResults(true);
    // Reset pagination state for new search
    setHasMoreNotes(true);
    try {
      const results = await searchNotes(searchTerm, typeFilter ? [typeFilter] : undefined, 50);

      // Upsert all results into noteCache to ensure they're available and up-to-date
      noteCache.upsertMany(results);

      // Store only the IDs - useNotes hook will get the actual data from cache
      setSearchResultIds(results.map((r) => r.id));

      // Update pagination state based on results
      setHasMoreNotes(results.length === 50);
    } catch (error) {
      // Log the full error for debugging
      uiLogger.error('Error fetching search results:', error);
      setSearchResultIds([]);
      setHasMoreNotes(false);
    } finally {
      setIsLoadingResults(false);
    }
  }, [searchTerm, typeFilter, searchNotes]);

  // Function to load more notes for infinite scrolling
  const loadMoreNotes = useCallback(async (): Promise<void> => {
    if (isLoadingMore || !hasMoreNotes || searchTerm.length > 0) return;

    setIsLoadingMore(true);
    try {
      // Since the database doesn't support offset-based pagination,
      // we'll fetch a larger batch and use the existing notes to determine if we have more
      const currentCount = searchResultIds.length;
      const batchSize = 50;
      const totalToFetch = currentCount + batchSize;

      const moreResults = await searchNotes('', typeFilter ? [typeFilter] : undefined, totalToFetch);

      // Upsert results into cache
      noteCache.upsertMany(moreResults);

      // Update IDs with the larger set
      setSearchResultIds(moreResults.map((r) => r.id));

      // If we got fewer notes than requested, we've reached the end
      setHasMoreNotes(moreResults.length === totalToFetch);
    } catch (error) {
      uiLogger.error('Error loading more notes:', error);
      setHasMoreNotes(false); // Stop trying to load more on error
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMoreNotes, searchTerm, typeFilter, searchNotes, searchResultIds.length]);

  // Use useEffect to call fetchSearchResults when initiated
  useEffect(() => {
    fetchSearchResults();
  }, [fetchSearchResults]);

  // Scroll event handler for infinite scrolling with throttling
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>): void => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold

      if (isNearBottom && hasMoreNotes && !isLoadingMore && !isLoadingResults && searchTerm.length === 0) {
        // Add a small delay to prevent rapid successive calls
        setTimeout(() => {
          if (hasMoreNotes && !isLoadingMore) {
            loadMoreNotes();
          }
        }, 100);
      }
    },
    [hasMoreNotes, isLoadingMore, isLoadingResults, searchTerm, loadMoreNotes],
  );

  const [{ isOver }, libRef] = useDrop<Note, void, { isOver: boolean }>({
    accept: ['TAKEAWAY', 'SOURCE', 'ANNOTE', 'CARD', 'LISTCARD'],
    drop: (item: Note, monitor) => {
      if (!monitor.didDrop()) {
        hide(item.id);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver() && monitor.canDrop(),
    }),
  });

  // Filter shown notes based on search term and type filter
  // Use useMemo so this only recalculates when dependencies change
  const selectedResults = useMemo(() => {
    // Filter out undefined notes
    let filtered = shownNotes.filter((note): note is Note => note !== undefined);

    // Apply type filter if present
    if (typeFilter) {
      filtered = filtered.filter((note) => note.type === typeFilter);
    }

    // Apply search term filter if present
    if (searchTerm.length > 0) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((note) => {
        const titleMatch = note.title?.toLowerCase().includes(lowerSearch);
        const valueMatch = note.value?.toLowerCase().includes(lowerSearch);
        return titleMatch || valueMatch;
      });
    }

    return filtered;
  }, [shownNotes, searchTerm, typeFilter]);

  // Filter and group the search results (exclude shown notes)
  useEffect(() => {
    const processSearchResults = (): void => {
      // Get non-selected results (exclude anything that's shown)
      const results = searchResults
        .filter((note) => note && !shownIds.includes(note.id))
        .filter((n): n is Note => n !== undefined);

      if (results.length === 0) {
        setGroupedSearchResults([]);
      } else if (searchTerm !== '') {
        // Search results
        setGroupedSearchResults([{ label: 'search results', minDate: '', notes: results }]);
      } else {
        // Latest notes grouped by date
        const localGroupedSearchResults = groupByDate(results);
        setGroupedSearchResults(localGroupedSearchResults);
      }
    };

    processSearchResults();
  }, [searchResults, shownIds, searchTerm]);

  // Auto-close library when mouse leaves if not pinned
  const handleMouseLeave = useCallback((): void => {
    if (!panels.lib.isPinned) {
      closePanel('lib');
    }
  }, [panels.lib.isPinned, closePanel]);

  const handleSelectType = useCallback(
    (type?: TypeOfNote) => (): void => {
      setTypeFilter(type);
    },
    [setTypeFilter],
  );

  const toggleSelectedExpanded = useCallback((): void => {
    setIsSelectedExpanded((prev) => {
      const newValue = !prev;
      localStorage.setItem(SELECTED_CARDS_EXPANDED_KEY, String(newValue));
      return newValue;
    });
  }, []);

  const selectCard = (note: Note): void => {
    show(note.id);
    setTimeout(() => {
      setFocus(note.id);
      scrollToNote(note.id);
    }, 50);
  };

  const setLibRef = useCallback(
    (element: HTMLDivElement | null) => {
      libRef(element);
    },
    [libRef],
  );

  return (
    <LibraryWrapper
      ref={setLibRef}
      onMouseEnter={() => setIsLibraryHovered(true)}
      onMouseLeave={() => {
        setIsLibraryHovered(false);
        handleMouseLeave();
      }}
    >
      {(isOver || isOverLibrary) && <HideDropZone>Hide card</HideDropZone>}
      <TypeRow>
        <TypeButton key="all" isSelected={!typeFilter} onClick={handleSelectType()}>
          All
        </TypeButton>
        {TYPES_OF_NOTES.map((noteType: TypeOfNote) => {
          return (
            <TypeButton key={noteType} isSelected={noteType === typeFilter} onClick={handleSelectType(noteType)}>
              {NOTE_TAB_LABELS[noteType]}
            </TypeButton>
          );
        })}
      </TypeRow>
      <CardGrid ref={containerRef} onScroll={handleScroll}>
        {isLoadingResults && searchTerm.length > 0 && (
          <div>
            <p>Loading...</p>
          </div>
        )}
        {!isLoadingResults && searchTerm.length > 0 && searchResultIds.length === 0 && (
          <div>
            <p>No results found</p>
          </div>
        )}
        {hoveredCard && previewRef && !isUserDragging && isLibraryHovered && (
          <PreviewCard note={hoveredCard} isTriggerHovered={true} triggerRef={previewRef} />
        )}
        {!isLoadingResults && groupedSearchResults.length === 0 && searchTerm.length > 0 && (
          <EmptyResponse>No notes found for "{searchTerm}"</EmptyResponse>
        )}
        {selectedResults.length > 0 && (
          <CardGroup key="selected">
            <SelectedGroupHeader onClick={toggleSelectedExpanded}>
              <SelectedGroupTitle>Selected cards ({selectedResults.length})</SelectedGroupTitle>
              <ChevronIcon $isExpanded={isSelectedExpanded} size={16} />
            </SelectedGroupHeader>
            {isSelectedExpanded && (
              <CardList style={{ paddingBottom: '3rem' }}>
                {selectedResults.map((note: Note) => (
                  <Fragment key={note.id}>
                    <OverlapWrapper>
                      <CardWrapper
                        key={`wrapper-${note.id}`}
                        data-testid={`library-note-card-${note.id}`}
                        onClick={() => selectCard(note)}
                        onMouseEnter={() => setHoveredCard(note)}
                        onMouseLeave={() => setHoveredCard(null)}
                        ref={note.id === hoveredCard?.id ? previewRef : null}
                      >
                        <ListCard note={note} />
                      </CardWrapper>
                    </OverlapWrapper>
                  </Fragment>
                ))}
              </CardList>
            )}
          </CardGroup>
        )}
        {groupedSearchResults.length > 0 &&
          groupedSearchResults.map((group) => (
            <CardGroup key={group.label}>
              <CardGroupTitle>{group.label}</CardGroupTitle>
              <CardList>
                {group.notes.map((note: Note, index: number) => (
                  <Fragment key={note.id}>
                    <CardWrapper
                      key={`wrapper-${note.id}`}
                      data-testid={`library-note-card-${note.id}`}
                      onClick={() => selectCard(note)}
                      onMouseEnter={() => setHoveredCard(note)}
                      onMouseLeave={() => setHoveredCard(null)}
                      ref={note.id === hoveredCard?.id ? previewRef : null}
                    >
                      <ListCard
                        key={`sc-${note.id}`}
                        note={note}
                        disabled={keepShownNotes && shownIds.includes(note.id)}
                      />
                      {index < group.notes.length - 1 && <DivideLine />}
                    </CardWrapper>
                  </Fragment>
                ))}
              </CardList>
            </CardGroup>
          ))}
        {isLoadingMore && (
          <div style={{ padding: '2rem', textAlign: 'center', color: colors.text.placeholder }}>
            Loading more notes...
          </div>
        )}
        {!hasMoreNotes && searchResultIds.length > 0 && searchTerm.length === 0 && (
          <div style={{ padding: '2rem', textAlign: 'center', color: colors.text.placeholder }}>
            No more notes to load
          </div>
        )}
      </CardGrid>
    </LibraryWrapper>
  );
};

export default Library;
